import { useMemo, useRef, useState } from "react";
import { parseAsString, useQueryState } from "nuqs";
import { apiClient } from "@groot/shell/lib/api";
import { toast } from "sonner";
import { useConfirm } from "@groot/ui/primitives";
import {
  useCreateFolder,
  useDeleteFiles,
  useDeleteFolder,
  useRenameFile,
  useStorageFiles,
  useUploadFile,
} from "./useStorage";

/** Download a file via a programmatic blob URL. */
export async function handleDownload(filePath: string, name: string): Promise<void> {
  try {
    const blob = await apiClient.getBlob("/storage/files/download", { filePath });
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = name;
    link.click();
    window.URL.revokeObjectURL(blobUrl);
    toast.success("Success", { description: "File downloaded successfully" });
  } catch (error) {
    console.error(error);
    toast.error("Download failed", { description: "Unable to download file" });
  }
}

/** Open a file in a new tab. Opens the tab before the await so the browser ties
 * it to the click's user activation — opening after an await is popup-blocked. */
export async function handleView(filePath: string): Promise<void> {
  const win = window.open("", "_blank");
  try {
    const blob = await apiClient.getBlob("/storage/files/download", { filePath });
    const blobUrl = window.URL.createObjectURL(blob);
    if (win) {
      win.location.href = blobUrl;
    } else {
      window.location.href = blobUrl; // popup blocked — fall back to same tab
    }
    // The new tab has loaded the blob by the time this fires; release it so the
    // blob data isn't pinned for the page's lifetime (matches handleDownload).
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 2000);
  } catch (error) {
    win?.close();
    console.error(error);
    toast.error("View failed", { description: "Unable to view file" });
  }
}

/** All state, mutations, and handlers for the Storage page. */
export function useStorageActions() {
  const [currentPath, setCurrentPath] = useQueryState("path", parseAsString.withDefault(""));
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState<{ key: string; name: string } | null>(null);
  const uploadInputRef = useRef<HTMLInputElement | null>(null);
  const confirm = useConfirm();

  const { data: files = [], isLoading, refetch: refetchFiles } = useStorageFiles(currentPath);
  const uploadFile = useUploadFile();
  const deleteFiles = useDeleteFiles();
  const deleteFolder = useDeleteFolder();
  const createFolder = useCreateFolder();
  const renameFile = useRenameFile();

  const breadcrumbs = useMemo(() => {
    if (!currentPath) return [];
    const parts = currentPath.split("/").filter(Boolean);
    return parts.map((part, index) => ({
      name: part,
      path: `${parts.slice(0, index + 1).join("/")}/`,
    }));
  }, [currentPath]);

  const fileCount = files.filter((f) => !f.isDirectory).length;

  const navigateToFolder = (path: string) => {
    setCurrentPath(path);
    setSelectedFiles(new Set());
  };

  const toggleFileSelection = (fileKey: string) => {
    const newSelection = new Set(selectedFiles);
    if (newSelection.has(fileKey)) {
      newSelection.delete(fileKey);
    } else {
      newSelection.add(fileKey);
    }
    setSelectedFiles(newSelection);
  };

  const selectAllFiles = () => {
    const allFileKeys = files.flatMap((f) => (f.isDirectory ? [] : [f.key]));
    setSelectedFiles(new Set(allFileKeys));
  };

  const clearSelection = () => setSelectedFiles(new Set());

  const handleUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const filesList = event.target.files;
    if (!filesList || !filesList.length) return;
    const files = Array.from(filesList);
    // Upload each file with the current folder prefix so multi-file uploads
    // land in the same folder as single-file uploads. (The bulk-upload endpoint
    // ignores folder targeting, so per-file uploads are used for correctness.)
    Promise.allSettled(
      files.map((file) => uploadFile.mutateAsync({ file, filePath: `${currentPath}${file.name}` })),
    )
      .then((results) => {
        const successCount = results.filter((r) => r.status === "fulfilled").length;
        const failureCount = results.length - successCount;
        if (files.length === 1) {
          if (failureCount) {
            toast.error("Upload failed", { description: "Unable to upload file" });
          } else {
            toast.success("Upload complete", {
              description: `${files[0].name} uploaded successfully`,
            });
          }
        } else {
          const description = `${successCount} uploaded${
            failureCount ? `, ${failureCount} failed` : ""
          }`;
          if (failureCount) {
            toast.error("Bulk upload complete", { description });
          } else {
            toast.success("Bulk upload complete", { description });
          }
        }
      })
      .finally(() => {
        event.target.value = "";
      });
  };

  const handleDeleteFile = async (key: string) => {
    try {
      await deleteFiles.mutateAsync([key]);
      toast.success("Deleted", { description: "File removed" });
    } catch (error) {
      console.error(error);
      toast.error("Delete failed", { description: "Unable to delete file" });
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedFiles.size === 0) return;
    if (
      !(await confirm({
        title: `Delete ${selectedFiles.size} file${selectedFiles.size === 1 ? "" : "s"}?`,
        description: "Selected files will be permanently removed.",
        confirmLabel: "Delete",
        destructive: true,
      }))
    )
      return;

    try {
      await deleteFiles.mutateAsync(Array.from(selectedFiles));
      toast.success("Deleted", { description: `${selectedFiles.size} file(s) removed` });
      clearSelection();
    } catch (error) {
      console.error(error);
      toast.error("Delete failed", { description: "Unable to delete files" });
    }
  };

  const handleDeleteFolder = async (folderKey: string) => {
    if (
      !(await confirm({
        title: `Delete folder "${folderKey}"?`,
        description: "The folder and everything inside it will be permanently deleted.",
        confirmLabel: "Delete",
        destructive: true,
      }))
    )
      return;
    try {
      await deleteFolder.mutateAsync(folderKey);
      toast.success("Folder deleted", { description: folderKey });
    } catch (error) {
      console.error(error);
      toast.error("Delete failed", { description: "Unable to delete folder" });
    }
  };

  const handleCreateFolder = async (name: string) => {
    const trimmed = name.trim().replace(/\/+$/u, "");
    if (!trimmed) return;
    const path = `${currentPath}${trimmed}/`;
    try {
      await createFolder.mutateAsync(path);
      toast.success("Folder created", { description: path });
      setFolderDialogOpen(false);
    } catch (error) {
      console.error(error);
      toast.error("Failed to create folder", { description: "Please try again" });
    }
  };

  const handleRename = async (newName: string) => {
    if (!renameTarget || !newName.trim()) return;
    const newPath = `${currentPath}${newName.trim()}`;
    try {
      await renameFile.mutateAsync({ oldPath: renameTarget.key, newPath });
      toast.success("File renamed", {
        description: `${renameTarget.name} → ${newName.trim()}`,
      });
      setRenameTarget(null);
      clearSelection();
    } catch (error) {
      console.error(error);
      toast.error("Rename failed", { description: "Unable to rename file" });
    }
  };

  const startRename = (key: string, name: string) => {
    setRenameTarget({ key, name });
  };

  return {
    files,
    isLoading,
    refetchFiles,
    breadcrumbs,
    fileCount,
    currentPath,
    selectedFiles,
    folderDialogOpen,
    setFolderDialogOpen,
    renameTarget,
    setRenameTarget,
    uploadInputRef,
    createFolder,
    renameFile,
    navigateToFolder,
    toggleFileSelection,
    selectAllFiles,
    clearSelection,
    handleUpload,
    handleDeleteFile,
    handleDeleteSelected,
    handleDeleteFolder,
    handleCreateFolder,
    handleRename,
    startRename,
  };
}
